/**
 * Shared chain-of-thought timeline primitives — used by both the
 * conversation-thread CoT (`agent-chain-of-thought.tsx`) and the delegated
 * task card (`AuiDelegatedTaskCard.tsx`). Keeping types + row components
 * here means both surfaces render identical labels (with `getToolDetail`
 * extraction) and apply the same dedup rules.
 *
 * Each surface still derives its own `TimelineEntry[]`:
 *   - The conversation CoT derives from the assistant-ui `parts` array
 *     in `deriveTimelineEntries` (`agent-chain-of-thought.tsx`).
 *   - The task card derives from streamed WebSocket events inside
 *     `useDelegatedTaskStatus`.
 * They just produce the same shape and feed the same renderer.
 */

import {
  MessageSquare,
  Users,
  Bot,
  Loader2,
  Check,
  XCircle,
  Circle,
  PanelRight,
} from 'lucide-react';
import { useToolDisplayName } from '../../hooks/useTools';
import { getToolIcon } from '../../utils/tool-icons';
import { InlineMarkdown } from '../ui/markdown-content';

/** Glanceable CoT narration is capped before rendering: line-clamp only hides
 *  visual overflow, but the full blob still parses and mounts in the DOM each
 *  streaming tick. ~400 chars sits above what 3 clamped lines show. */
function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export type TimelineEntry =
  | { kind: 'narration'; id: string; text: string }
  | { kind: 'tool-call'; id: string; toolName: string; toolDisplayName?: string; detail?: string }
  | { kind: 'subagent-group'; id: string; children: SubAgentChild[] }
  | { kind: 'waiting'; id: string }
  /** Task-card final-answer phase placeholder ("Producing output…"). The
   *  conversation CoT collapses on `final_answer_start` and never emits this. */
  | { kind: 'responding'; id: string }
  /** A single sub-agent rendered as its own top-level timeline row. Used by
   *  the delegated-task card so each spawned sub-agent participates in the
   *  rolling window individually (sorted by recency), instead of being
   *  collapsed into a `subagent-group` block as the conversation CoT does. */
  | { kind: 'subagent-row'; id: string; child: SubAgentChild };

export interface SubAgentChild {
  agentName: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  summary?: string;
  items: Array<
    | { kind: 'narration'; id: string; text: string }
    | { kind: 'tool-call'; id: string; toolName: string; toolDisplayName?: string; detail?: string }
  >;
}

/** Shape of `_progress` / `args` payload used by the CoT to summarize a
 *  running sub-agent. Mirrors `SubAgentState` fields used by the renderer. */
export interface SubAgentResult {
  status?: string;
  agentName?: string;
  reasoningText?: string;
  responseText?: string;
  toolCalls?: Array<{ toolName: string; toolCallId: string; label?: string }>;
  error?: string;
}

// `toolCallEntryLabel` + the timeline-derivation logic live in the
// dependency-light `./cot-derive` module (no lucide-react / markdown imports)
// so they stay unit-testable. Re-exported here to preserve the public API.
export { toolCallEntryLabel, deriveTimelineEntries, buildSubAgentMiniTimeline } from './cot-derive';

// ─── Sub-components ─────────────────────────────────────────────────────────

/** Renders a tool-call entry with a complete action phrase or fallback label. */
export function ToolCallItem({
  toolName,
  toolDisplayName,
  detail,
}: {
  toolName: string;
  toolDisplayName?: string;
  /** Full action phrase from getToolDetail (e.g. "Retrieving step 5"). */
  detail?: string;
}) {
  const displayName = useToolDisplayName(toolName, toolDisplayName);
  const Icon = getToolIcon(toolName);
  return (
    <div className="flex items-center gap-2 text-sm text-foreground/70">
      <Icon className="h-3.5 w-3.5 shrink-0 text-foreground/35" />
      <span className="truncate">
        {detail ? detail : <>Using {displayName}...</>}
      </span>
    </div>
  );
}

/** Status icon for a sub-agent child. Color is neutral foreground across all
 *  states — the icon shape (and animation, for running) carries the meaning. */
export function SubAgentStatusIcon({ status }: { status: SubAgentChild['status'] }) {
  switch (status) {
    case 'running':
      return <Loader2 className="h-3 w-3 shrink-0 animate-spin text-foreground/35" />;
    case 'completed':
      return <Check className="h-3 w-3 shrink-0 text-foreground/35" />;
    case 'failed':
      return <XCircle className="h-3 w-3 shrink-0 text-foreground/35" />;
    default:
      return <Circle className="h-3 w-3 shrink-0 text-foreground/25" />;
  }
}

/** Renders a single sub-agent within a group as one inline row:
 *  [Bot] {agentName} • [LastItemIcon] {lastActionText} [Status]
 *  When there's no item yet, the bullet + last action half is omitted. */
export function SubAgentChildRow({ child }: { child: SubAgentChild }) {
  const lastItem = child.items[child.items.length - 1];
  const lastIcon = lastItem
    ? lastItem.kind === 'narration'
      ? MessageSquare
      : getToolIcon(lastItem.toolName)
    : null;
  const lastText = !lastItem
    ? null
    : lastItem.kind === 'narration'
      ? lastItem.text
      : (lastItem.detail ?? `Using ${lastItem.toolDisplayName || lastItem.toolName}`);

  return (
    <div className="ml-2 mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Bot className="h-3 w-3 shrink-0 text-foreground/25" />
      <span className="shrink-0 font-medium">{child.agentName}</span>
      {lastText && lastIcon && (
        <>
          <span className="shrink-0 text-foreground/25">•</span>
          {(() => {
            const Icon = lastIcon;
            return <Icon className="h-2.5 w-2.5 shrink-0 text-foreground/25" />;
          })()}
          <span className="min-w-0 truncate text-foreground/70">{lastText}</span>
        </>
      )}
      <SubAgentStatusIcon status={child.status} />
    </div>
  );
}

/** Renders a single timeline entry based on its kind. */
export function TimelineEntryRow({ entry }: { entry: TimelineEntry }) {
  switch (entry.kind) {
    case 'narration':
      return (
        <div className="flex items-start gap-2 text-sm text-foreground/95">
          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground/35" />
          <div className="line-clamp-3 min-w-0"><InlineMarkdown className="text-sm text-foreground/95">{truncate(entry.text, 400)}</InlineMarkdown></div>
        </div>
      );
    case 'tool-call':
      return (
        <ToolCallItem
          toolName={entry.toolName}
          toolDisplayName={entry.toolDisplayName}
          detail={entry.detail}
        />
      );
    case 'subagent-group':
      return (
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-3.5 w-3.5 shrink-0 text-foreground/35" />
            <span>Sub Agents</span>
          </div>
          {entry.children.map((child, i) => (
            <SubAgentChildRow key={`${child.agentName}-${i}`} child={child} />
          ))}
        </div>
      );
    case 'waiting':
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-foreground/35" />
          <span>Waiting on sub-agents to complete</span>
        </div>
      );
    case 'responding':
      return (
        <div className="flex items-center gap-2 text-sm text-foreground/70">
          <PanelRight className="h-3.5 w-3.5 shrink-0 text-foreground/35" />
          <span>Producing output...</span>
        </div>
      );
    case 'subagent-row':
      return <SubAgentChildRow child={entry.child} />;
  }
}
